import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Payment form component
const PaymentForm = ({ amount, onSuccess, onCancel, appointmentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/appointments`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
      } else {
        // Payment successful
        onSuccess();
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4">
        <div className="text-lg font-semibold text-gray-900 mb-2">
          Amount to charge: ${amount.toFixed(2)}
        </div>
      </div>

      <PaymentElement />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 btn btn-primary"
        >
          {processing ? (
            <>
              <Loader className="animate-spin mr-2" size={16} />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="mr-2" size={16} />
              Pay ${amount.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Main modal component
const StripePaymentModal = ({ isOpen, onClose, amount, onSuccess, appointmentId }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initializePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get Stripe publishable key
      const token = sessionStorage.getItem('token');
      const configResponse = await axios.get(`${API_URL}/payment-settings/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const stripeProvider = configResponse.data.providers?.find(p => p.provider === 'stripe');
      
      if (!stripeProvider || !stripeProvider.public_key) {
        throw new Error('Stripe is not configured');
      }

      // Initialize Stripe
      const stripe = await loadStripe(stripeProvider.public_key);
      setStripePromise(stripe);

      // Create payment intent
      const intentResponse = await axios.post(
        `${API_URL}/payments/create-payment-intent`,
        { amount, appointmentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setClientSecret(intentResponse.data.clientSecret);
      setLoading(false);
    } catch (err) {
      console.error('Initialize payment error:', err);
      setError(err.response?.data?.error || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      initializePayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, amount]);

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <CreditCard className="text-primary-600" size={24} />
            <h2 className="text-xl font-bold">Stripe Payment</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="animate-spin text-primary-600 mb-4" size={32} />
              <p className="text-gray-600">Initializing payment...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              <p className="font-semibold mb-2">Error</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 btn btn-secondary w-full"
              >
                Close
              </button>
            </div>
          )}

          {!loading && !error && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm
                amount={amount}
                onSuccess={handleSuccess}
                onCancel={onClose}
                appointmentId={appointmentId}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentModal;
